import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, ArrowRight, CheckCircle2, Circle, DollarSign, Sparkles } from "lucide-react";
import { useAuth, useGet } from "@/context/AuthContext";
import { money, pct, int } from "@/lib/fmt";
import { KpiCard, Loading, ErrorBox, Section, EmptyState } from "@/components/app/Bits";
import { ErrorBoundary } from "@/components/app/AppStates";
import { Chart, moneyK } from "@/components/app/Chart";
import { useIsMobile } from "@/hooks/useIsMobile";
import TrialProgress from "@/components/app/TrialProgress";
import { SetupProgress } from "@/components/app/Brief";

export function Attention({ items }) {
  if (!items?.length) return <div className="card-surface p-5 text-sm text-stone-600 flex items-center gap-2" data-testid="attention-empty"><CheckCircle2 className="h-4 w-4 text-emerald-700" /> Nothing needs attention right now.</div>;
  return (
    <div className="card-surface divide-y divide-[#E6E1D7]" data-testid="needs-attention">
      {items.map((a, i) => (
        <Link to={a.link} key={i} data-testid={`attention-item-${i}`} className="flex items-start gap-3 p-4 hover:bg-[#FBF9F5]">
          {a.level === "action" ? <AlertOctagon className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5 text-stone-500">{a.level === "action" ? "⚠ Action Needed" : "Watch"}</div>
            <div className="text-sm font-medium text-stone-900">{a.text}</div>
            <div className="text-xs text-stone-500 mt-0.5">→ {a.action}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function Activation() {
  const { data } = useGet("/activation");
  if (!data || data.complete) return null;
  return (
    <div className="card-surface p-5 mb-8" data-testid="activation-progress">
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium">Finish setting up — {data.done_count} of 5 done</div>
        <div className="h-2 w-32 bg-[#F3EFE6] rounded-full overflow-hidden"><div className="h-full bg-[#9E2A2B]" style={{ width: `${data.done_count * 20}%` }} /></div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {data.steps.map((s) => (
          <Link key={s.key} to={s.link} data-testid={`activation-${s.key}`} className={`flex items-center gap-2 text-sm rounded-lg p-2 ${s.done ? "text-stone-400 line-through" : "hover:bg-[#FBF9F5]"}`}>
            {s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <Circle className="h-4 w-4" />} {s.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function Digest() {
  const { data } = useGet("/digest");
  if (!data) return null;
  return (
    <Link to={`/app/report?week=${data.week_start}`} data-testid="last-week-card" className="block rounded-xl bg-[#1C1917] text-stone-300 p-5 md:p-6 mb-8 hover:bg-stone-900">
      <div className="flex items-center gap-2 label-eyebrow text-stone-500"><Sparkles className="h-3.5 w-3.5" /> Your Restaurant Last Week</div>
      <div className="flex flex-wrap gap-x-8 gap-y-2 mt-3 text-sm">
        {data.sales != null && <span><b className="text-white num">{money(data.sales)}</b> · {data.goal_pct}% of goal · {data.prev_change_pct >= 0 ? "+" : ""}{data.prev_change_pct}% vs prior week</span>}
        <span>Waste <b className="text-white num">{money(data.waste)}</b></span>
        {data.discounts_pct != null && <span>Discounts <b className="text-white">{pct(data.discounts_pct)}</b></span>}
        <span>{data.issues} operational issues · {data.outstanding_tasks} outstanding tasks</span>
      </div>
      {data.summary?.sections?.[0]?.bullets?.[0] && <p className="text-sm text-stone-400 mt-3 line-clamp-2">{data.summary.sections[0].bullets[0].text}</p>}
      <div className="text-xs text-white mt-3 inline-flex items-center gap-1">Open the full report <ArrowRight className="h-3 w-3" /></div>
    </Link>
  );
}

const CHARTS = [
  { key: "daily", label: "Daily sales", src: "daily", dk: "net_sales", fmt: moneyK, fin: true },
  { key: "weekly", label: "Sales vs goal", src: "weekly", dk: "net_sales", goal: "goal", type: "bar", fmt: moneyK, fin: true },
  { key: "disc", label: "Discounts %", src: "weekly", dk: "discount_pct", fmt: (v) => `${v}%`, fin: true },
  { key: "waste", label: "Waste", src: "weekly", dk: "waste", type: "bar", fmt: moneyK },
  { key: "avg", label: "Spend per guest", src: "weekly", dk: "avg_check", fmt: (v) => `$${v}`, fin: true },
  { key: "guests", label: "Guest count", src: "weekly", dk: "guests", type: "bar", fmt: int },
];

export default function Dashboard() {
  const { user, org, financial } = useAuth();
  const { data, isLoading, error, refetch } = useGet("/dashboard");
  const [chart, setChart] = useState(financial ? "daily" : "waste");
  const [allKpis, setAllKpis] = useState(false);
  const mobile = useIsMobile();
  const [showTrends, setShowTrends] = useState(false);
  const h = new Date().getHours();
  const greet = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  const header = (
    <div className="mb-8">
      <div className="label-eyebrow mb-2">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
      <h1 className="text-3xl md:text-4xl font-display font-semibold" data-testid="dashboard-greeting">{greet}, {user?.name?.split(" ")[0]}.</h1>
      <p className="text-stone-600 mt-1">Here's what's happening at {data?.location?.name || org?.name}.</p>
    </div>
  );
  const widgets = <><ErrorBoundary inline><TrialProgress /></ErrorBoundary><ErrorBoundary inline><SetupProgress /></ErrorBoundary></>;
  if (isLoading) return <div data-testid="dashboard-page">{header}<Loading label="Loading today's numbers…" /></div>;
  if (error || !data) return <div data-testid="dashboard-page">{header}{widgets}<ErrorBox error={error} onRetry={refetch} /></div>;
  const allK = data.kpis || [];
  const c = CHARTS.find((x) => x.key === chart) || CHARTS[3];
  const kpis = allKpis ? allK : allK.slice(0, mobile ? 4 : 8);
  return (
    <div data-testid="dashboard-page">
      {header}
      {widgets}
      {!data.has_sales && financial && (
        <EmptyState icon={DollarSign} title="No sales entered yet" text="Enter yesterday's numbers or import a CSV from your POS and your dashboard will come alive."
          action={<Link to="/app/sales" className="h-11 px-5 rounded-lg bg-stone-900 text-white text-sm font-semibold inline-flex items-center" data-testid="dashboard-enter-sales">Enter daily sales</Link>} />
      )}
      <Section title="Needs Attention" testid="attention-section"><ErrorBoundary inline><Attention items={data.attention} /></ErrorBoundary></Section>
      <ErrorBoundary inline><Digest /></ErrorBoundary>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-3" data-testid="kpi-grid">
        {kpis.map((k) => <KpiCard key={k.key} k={k} />)}
      </div>
      {allK.length > kpis.length || allKpis ? <button className="text-sm font-medium underline mb-8 min-h-[40px]" data-testid="kpi-toggle" onClick={() => setAllKpis(!allKpis)}>{allKpis ? "Show fewer" : `Show all ${allK.length} metrics`}</button> : null}
      <Section title="Trends" testid="charts-section">
        {mobile && !showTrends && <button className="w-full card-surface min-h-[52px] text-sm font-medium" onClick={() => setShowTrends(true)} data-testid="trends-toggle">Show trends</button>}
        {(!mobile || showTrends) && <div className="card-surface p-4 md:p-6">
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
            {CHARTS.filter((x) => financial || !x.fin).map((x) => (
              <button key={x.key} data-testid={`chart-tab-${x.key}`} onClick={() => setChart(x.key)}
                className={`whitespace-nowrap h-9 px-4 rounded-full text-sm border ${chart === x.key ? "bg-stone-900 text-white border-stone-900" : "bg-white border-[#E6E1D7]"}`}>{x.label}</button>
            ))}
          </div>
          <ErrorBoundary inline><Chart type={c.type} data={data[c.src] || []} dataKey={c.dk} goalKey={c.goal} fmt={c.fmt} /></ErrorBoundary>
        </div>}
      </Section>
    </div>
  );
}
